<template>
  <!-- Table -->
  <va-data-table
    :no-data-html="$t('activations.showActivationsEventsModal.noItems')"
    :items="state.activations"
    :columns="state.columns"
    class="table-scroll"
  >
    <template #header(type)>
      {{ $t("activations.showActivationsEventsModal.type") }}
    </template>
   
    <template #header(actionAt)>
      {{ $t("activations.showActivationsEventsModal.actionAt") }}
    </template>
    <template #header(initiatedBy)>
      {{ $t("activations.showActivationsEventsModal.initiatedBy") }}
    </template>
    <template #header(hostName)>
      {{ $t("activations.showActivationsEventsModal.hostName") }}
    </template>
    <template #header(aliasName)>
      {{ $t("activations.showActivationsEventsModal.aliasName") }}
    </template>
    <template #header(id)>
      {{ $t("activations.showActivationsEventsModal.detail") }}
    </template>

    <template #cell(type)="{ source: type }">
      <span ref="table">
        <va-badge :text="type.label" :color="type.color" />
      </span>
    </template>
    <template
      #cell(initiatedBy)="{ source: initiatedBy }"
      v-if="initiatedBy !== null"
    >
      <div class="col-icon">
        <span>
          {{ initiatedBy.type }}
        </span>
        <button-clipboard-copy
          v-if="initiatedBy.id"
          :field="$t('activations.userId')"
          :value="initiatedBy.id"
        />
      </div>
    </template>
    <template #cell(actionAt)="{ source: actionAt }">
      <span>
        {{ actionAt }}
      </span>
    </template>
    <template #cell(reason)="{ source: reason }">
      <span>
        {{ reason }}
      </span>
    </template>
    <template #cell(hostName)="{ source: hostName }">
      <span>
        {{ hostName }}
      </span>
    </template>
    <template #cell(aliasName)="{ source: aliasName }">
      <span>
        {{ aliasName }}
      </span>
    </template>
    <template #cell(id)="{ source: id }">
      <va-button
        flat
        size="small"
        icon="arrow_forward"
        @click="goToEventDetail(id)"
      />
    </template>

    <template #bodyAppend>
      <tr>
        <td colspan="7">
          <va-inner-loading class="mt-3" :loading="state.loading" />
          <p v-if="state.allLogsFetched" class="text-center">
            {{ $t("activations.showActivationsEventsModal.allItemsFetched") }}
          </p>
        </td>
      </tr>
    </template>
  </va-data-table>
  <show-activation-event-details-modal
    :is-open="state.showDetailsModal"
    :event="state.currentEvent"
    @cancel="state.showDetailsModal = false"
    @validate="validate()"
  />
</template>

<script setup>
import { computed, reactive, watch, onMounted, inject } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  callFetchActivationsEvents,
  callFetchActivationEvent
} from "@/api/auditing";
import { printDate } from "@/utils";
import ButtonClipboardCopy from "@/components/ButtonClipboardCopy.vue";
import { ACTIVATIONS_LOGS_TYPE_COLORS } from "@/constants";
import ShowActivationEventDetailsModal from "@/components/modal/ShowActivationEventDetailsModal.vue";

const handleError = inject("handleError");
const route = useRoute();
const { t } = useI18n();
let table = null;

const state = reactive({
  activations: [],
  columns: computed(() => {
    return [
      { key: "type" },
      { key: "actionAt" },
      { key: "initiatedBy" },
      { key: "hostName" },
      { key: "aliasName" },
      { key: "id" }
    ];
  }),
  loading: false,
  currentPage: 1,
  perPage: 100,
  totalPages: 0,
  allLogsFetched: false,
  showDetailsModal: false,
  currentEvent: null
});

/**
 * Load activations events from API.
 * @param {number} page - page number
 */
async function loadActivationsEvents(page = 1) {
  state.loading = true;

  const params = {
    count: state.perPage,
    page,
    entitlementId: route.params.id
  };

  if (!state.allLogsFetched) {
    try {
      const resp = await callFetchActivationsEvents({ params });
      if (resp?.data) {
        const newData = resp.data.items.map(item => {
          return {
            id: item?.id,
            type: {
              label: item?.type,
              color: ACTIVATIONS_LOGS_TYPE_COLORS[item?.type]
            },
            actionAt: printDate(item?.triggeredAt),
            initiatedBy: {
              id: item?.initiatedBy?.id,
              type: item?.initiatedBy?.type ?? t("general.unknown")
            },
            hostName: item?.data?.state?.hostName,
            aliasName: item?.data?.state?.aliasName
          };
        });

        if (newData.length === 0) {
          state.allLogsFetched = true;
        }
        state.activations = state.activations.concat(newData);
      }
    } catch (e) {
      handleError(e?.response?.data);
    }
  }

  state.loading = false;
}

/**
 * Get user id from account id.
 * @param {string} accountId - account id
 * @return {string} - user id
 */
async function fetchEvent(eventId) {
  try {
    const resp = await callFetchActivationEvent(eventId);
    return resp.data;
  } catch (e) {
    handleError(e);
  }
}

async function goToEventDetail(eventId) {
  state.currentEvent = await fetchEvent(eventId);
  state.showDetailsModal = true;
}

const validate = () => {
  state.showDetailsModal = false;
};

const detectScrolledBottom = () => {
  if (table.scrollTop + table.clientHeight >= table.scrollHeight) {
    state.currentPage++;
  }
};

watch(
  () => state.currentPage,
  async currentPage => {
    await loadActivationsEvents(currentPage);
  }
);

onMounted(async () => {
  table = document.querySelector(".table-scroll");
  table.addEventListener("scroll", detectScrolledBottom);
  await loadActivationsEvents();
});
</script>

<style lang="scss" scoped>
.table-scroll {
  max-height: 25rem;
  display: block;
  overflow: overlay;
  padding-right: 1rem;
}

.col-icon {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}
</style>
